import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Bell, 
  Send, 
  RefreshCw, 
  Smartphone, 
  Users, 
  Clock,
  CheckCircle,
  XCircle,
  Megaphone
} from 'lucide-react';

const UpdateManagement = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [subscriptionCount, setSubscriptionCount] = useState(0);
  const [notificationLogs, setNotificationLogs] = useState([]);
  
  // Form state for sending update notification
  const [updateForm, setUpdateForm] = useState({
    version: '',
    release_notes: '',
    force_update: false
  });

  // Form state for custom notification
  const [customNotification, setCustomNotification] = useState({
    title: '',
    body: ''
  });

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch version info
      const versionRes = await api.get('/push/version');
      setVersionInfo(versionRes.data);
      setUpdateForm(prev => ({ ...prev, version: versionRes.data.version }));
      
      // Fetch subscription count
      try {
        const subRes = await api.get('/push/subscriptions');
        setSubscriptionCount(subRes.data.total || 0);
      } catch (e) {
        console.log('Could not fetch subscription count');
      }
      
      // Fetch notification logs
      try {
        const logsRes = await api.get('/push/notification-logs');
        if (logsRes.data.logs) {
          setNotificationLogs(logsRes.data.logs);
        }
      } catch (e) {
        console.log('Could not fetch notification logs');
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSendUpdateNotification = async () => {
    if (!updateForm.version) {
      toast.error('يرجى إدخال رقم الإصدار');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/push/notify-update', updateForm);
      toast.success(`تم إرسال ${response.data.sent} إشعار بنجاح`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الإشعارات');
    } finally {
      setSending(false);
    }
  };

  const handleSendCustomNotification = async () => {
    if (!customNotification.title || !customNotification.body) {
      toast.error('يرجى إدخال العنوان والمحتوى');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/push/send-all', customNotification);
      toast.success(`تم إرسال ${response.data.sent_count} إشعار بنجاح`);
      setCustomNotification({ title: '', body: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الإشعارات');
    } finally {
      setSending(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Bell className="w-8 h-8 text-primary" />
          إدارة التحديثات والإشعارات
        </h1>
        <p className="text-muted-foreground mt-2">إرسال إشعارات التحديث والإشعارات المخصصة للمستخدمين</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-primary/20 p-3 rounded-lg">
              <RefreshCw className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">الإصدار الحالي</p>
              <p className="text-2xl font-bold text-primary">{versionInfo?.version || '-'}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-green-500/20 p-3 rounded-lg">
              <Smartphone className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">الأجهزة المشتركة</p>
              <p className="text-2xl font-bold text-green-500">{subscriptionCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="bg-blue-500/20 p-3 rounded-lg">
              <Clock className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">تاريخ التحديث</p>
              <p className="text-lg font-bold text-blue-500">{versionInfo?.release_date || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Send Update Notification */}
      <div className="bg-card border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <RefreshCw className="w-6 h-6 text-primary" />
          <h2 className="text-xl font-semibold">إرسال إشعار تحديث</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">رقم الإصدار</label>
            <Input
              value={updateForm.version}
              onChange={(e) => setUpdateForm({ ...updateForm, version: e.target.value })}
              placeholder="مثال: 2.0.0"
              className="bg-secondary/50 border-white/10"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">ملاحظات التحديث</label>
            <Input
              value={updateForm.release_notes}
              onChange={(e) => setUpdateForm({ ...updateForm, release_notes: e.target.value })}
              placeholder="وصف التحديث..."
              className="bg-secondary/50 border-white/10"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={updateForm.force_update}
              onChange={(e) => setUpdateForm({ ...updateForm, force_update: e.target.checked })}
              className="w-4 h-4 accent-primary"
            />
            <span className="text-sm">تحديث إجباري</span>
          </label>
        </div>

        <Button
          onClick={handleSendUpdateNotification}
          disabled={sending || subscriptionCount === 0}
          className="mt-6 bg-primary"
        >
          {sending ? (
            <RefreshCw className="w-4 h-4 me-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 me-2" />
          )}
          إرسال إشعار التحديث
        </Button>
      </div>

      {/* Send Custom Notification */}
      <div className="bg-card border border-white/10 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Megaphone className="w-6 h-6 text-yellow-500" />
          <h2 className="text-xl font-semibold">إرسال إشعار مخصص</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">عنوان الإشعار</label>
            <Input
              value={customNotification.title}
              onChange={(e) => setCustomNotification({ ...customNotification, title: e.target.value })}
              placeholder="مثال: عرض خاص!"
              className="bg-secondary/50 border-white/10"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">محتوى الإشعار</label>
            <Input
              value={customNotification.body}
              onChange={(e) => setCustomNotification({ ...customNotification, body: e.target.value })}
              placeholder="اكتب محتوى الإشعار..."
              className="bg-secondary/50 border-white/10"
            />
          </div>
        </div>

        <Button
          onClick={handleSendCustomNotification}
          disabled={sending || subscriptionCount === 0}
          variant="outline"
          className="mt-6 border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
        >
          {sending ? (
            <RefreshCw className="w-4 h-4 me-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 me-2" />
          )}
          إرسال الإشعار
        </Button>
      </div>

      {/* Notification Logs */}
      {notificationLogs.length > 0 && (
        <div className="bg-card border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-muted-foreground" />
            <h2 className="text-xl font-semibold">سجل الإشعارات</h2>
          </div>

          <div className="space-y-4">
            {notificationLogs.map((log, index) => (
              <div 
                key={log.id || index}
                className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-full ${log.type === 'app_update' ? 'bg-primary/20' : 'bg-yellow-500/20'}`}>
                    {log.type === 'app_update' ? (
                      <RefreshCw className="w-4 h-4 text-primary" />
                    ) : (
                      <Megaphone className="w-4 h-4 text-yellow-500" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{log.title || `تحديث ${log.version}`}</p>
                    <p className="text-sm text-muted-foreground">
                      بواسطة {log.sent_by_name} - {new Date(log.created_at).toLocaleString('ar-SA')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-500">
                    <CheckCircle className="w-4 h-4" />
                    {log.sent_count}
                  </span>
                  {log.failed_count > 0 && (
                    <span className="flex items-center gap-1 text-red-500">
                      <XCircle className="w-4 h-4" />
                      {log.failed_count}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <p className="text-sm text-blue-400">
          <strong>ملاحظة:</strong> الإشعارات تُرسل فقط للمستخدمين الذين قاموا بتفعيل الإشعارات في متصفحهم. 
          يمكن للمستخدمين تفعيل الإشعارات من صفحة الإعدادات.
        </p>
      </div>
    </div>
  );
};

export default UpdateManagement;
